<template>
    <div
        ref="lateralMenu"
        class="ReaderSidebarPlugin fixed bottom-2 right-2 top-2 overflow-hidden rounded-xl border border-theme-border shadow-xl"
        :class="{ uiHidden: uiHidden }"
        dusk="ReaderSidebarPlugin"
    >
        <transition name="slide-fade">
            <div
                v-if="shouldRender"
                v-show="shouldShow"
                class="ReaderSidebarPluginContent"
                :class="{ uiOpen: shouldShow }"
                dusk="ReaderSidebarPluginContent"
            >
                <div class="container p-0">
                    <div class="row">
                        <div class="col">
                            <button
                                class="flatButton flex p-4"
                                type="button"
                                :aria-label="localizations[lang].tooltips.close"
                                @click="trackTogglePlugin"
                            >
                                <svg-icon icon="chevron-double-right"></svg-icon>
                            </button>
                        </div>
                    </div>

                    <div class="sidebar-grid container p-0" :class="{ debugMode: isDebugMode }">
                        <div class="w-full">
                            <div v-if="isDebugMode" class="debugger-controls">
                                <input
                                    v-model="testEventMessage"
                                    type="text"
                                    placeholder="Enter your message"
                                    aria-label="Debug test message"
                                />
                                <button
                                    class="focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                                    @click="pushTestEvent"
                                >
                                    send
                                </button>
                                <button
                                    class="reload-button focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                                    @click="reloadIframe"
                                >
                                    reload
                                </button>
                            </div>
                        </div>
                        <iframe
                            ref="pluginIframe"
                            :title="localizations[lang].tooltips.plugin || 'Plugin'"
                            class="h-full w-full"
                            sandbox="allow-scripts allow-same-origin allow-forms"
                        ></iframe>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { v7 as uuidv7 } from 'uuid';

export default {
    data() {
        return {
            nonce: null,
            // Here for debugging purposes
            // iframeSrc: 'https://volpe.localhost/third-party/third-party-plugin-debugger.html',
            iframeSrc: null,
            iframeSrcWithNonce: '',
            iframeLoaded: false,
            testEventMessage: '',
        };
    },
    computed: {
        ...mapState(['uiHidden', 'sidebar', 'publication', 'ui', 'isOffline', 'localizations', 'lang', 'fileType']),
        ...mapGetters(['isPreviewMode', 'currentPage', 'currentPages', 'publicationNumberOfPages']),
        shouldRender() {
            // return true;
            return this.ui.plugin.enabled && this.ui.plugin.src;
        },
        shouldShow() {
            // return true;
            return this.ui.plugin.opened;
        },
        iframeSrcSeparator() {
            // Determine the separator based on whether '?' is found in the src
            return this.iframeSrc?.includes('?') ? '&' : '?';
        },
        isDebugMode() {
            /**
             * Enable debug mode and it's UI when the iframe's SRC is from our debugger.
             * This is not a precise check, it could be triggered by other URLs and
             * that's fine.
             */
            return this.iframeSrc?.includes('third-party-plugin-debugger');
        },
        shouldInitialize() {
            // Only initialize when the plugin is both enabled and opened for the first time
            return this.shouldRender && this.shouldShow && !this.iframeLoaded;
        },
        currentTheme() {
            // Get the currently selected theme (dark/light)
            return this.ui.themeSelected || 'light';
        },
    },
    watch: {
        currentPages(currentPages, lastCurrentPages) {
            console.debug('[ReaderSidebarPlugin] Page changed', { currentPages, lastCurrentPages });
            if (!this.iframeLoaded) {
                console.debug('[ReaderSidebarPlugin] Plugin iframe not loaded, skipping page change event');
                return;
            }
            this.pushPageChange(currentPages);
        },
        shouldInitialize(shouldInit) {
            console.debug('[ReaderSidebarPlugin] Should initialize changed:', shouldInit);
            if (shouldInit) {
                console.debug('[ReaderSidebarPlugin] Initializing iframe for the first time');
                this.initializeIframe();
            }
        },
        currentTheme(newTheme, oldTheme) {
            console.debug('[ReaderSidebarPlugin] Theme changed', { newTheme, oldTheme });
            if (!this.iframeLoaded) {
                console.debug('[ReaderSidebarPlugin] Plugin iframe not loaded, skipping theme change event');
                return;
            }
            this.pushThemeChange(newTheme);
        },
    },
    mounted() {
        console.debug('[ReaderSidebarPlugin] Component mounted');
    },
    beforeDestroy() {
        this.iframeLoaded = false;
    },
    methods: {
        ...mapMutations(['closeSidebar']),
        ...mapActions(['trackEvent', 'togglePlugin']),
        initIframeConnection() {
            console.debug('[ReaderSidebarPlugin] Iframe loaded, establishing connection', {
                payload: {
                    currentPages: this.currentPages,
                    totalPages: this.publicationNumberOfPages,
                    contentType: this.fileType,
                    darkMode: this.currentTheme === 'dark',
                },
            });
            this.iframeLoaded = true;
            this.$refs.pluginIframe.contentWindow.postMessage(
                {
                    type: 'event',
                    name: 'init',
                    id: uuidv7(),
                    timestamp: Date.now(),
                    nonce: this.nonce,
                    payload: {
                        currentPages: this.currentPages,
                        totalPages: this.publicationNumberOfPages,
                        contentType: this.fileType,
                        darkMode: this.currentTheme === 'dark',
                    },
                },
                this.iframeSrcWithNonce
            );
        },
        pushPageChange(currentPages) {
            this.$refs.pluginIframe.contentWindow.postMessage(
                {
                    type: 'event',
                    name: 'page-change',
                    id: uuidv7(),
                    timestamp: Date.now(),
                    nonce: this.nonce,
                    payload: {
                        currentPages,
                    },
                },
                this.iframeSrcWithNonce
            );
        },
        pushThemeChange(theme) {
            this.$refs.pluginIframe.contentWindow.postMessage(
                {
                    type: 'event',
                    name: 'theme-change',
                    id: uuidv7(),
                    timestamp: Date.now(),
                    nonce: this.nonce,
                    payload: {
                        darkMode: theme === 'dark',
                    },
                },
                this.iframeSrcWithNonce
            );
        },
        pushTestEvent() {
            const message = {
                type: 'event',
                name: 'test',
                id: uuidv7(),
                timestamp: Date.now(),
                nonce: this.nonce,
                payload: {
                    currentPages: this.currentPages,
                },
            };

            if (this.testEventMessage.trim() !== '') {
                message.payload.testEventMessage = this.testEventMessage;
                this.testEventMessage = '';
            }

            this.$refs.pluginIframe.contentWindow.postMessage(message, this.iframeSrcWithNonce);
        },
        trackTogglePlugin() {
            this.trackEvent({
                action: 'Plugin - toggle',
                trigger: 'click',
                page: '',
                source: 'close corner',
            });
            this.togglePlugin();
        },
        resetIframeSrc() {
            this.nonce = uuidv7();
            this.iframeSrcWithNonce = `${this.iframeSrc}${this.iframeSrcSeparator}nonce=${this.nonce}`;
            this.$refs.pluginIframe.src = this.iframeSrcWithNonce;
        },
        reloadIframe() {
            this.iframeLoaded = false;
            this.resetIframeSrc();
        },
        initializeIframe() {
            // Initialize the iframe source only when needed
            this.iframeSrc = this.ui.plugin.src;
            this.resetIframeSrc();
            this.$refs.pluginIframe.onload = this.initIframeConnection;
        },
    },
};
</script>

<style lang="scss" scoped>
.slide-fade-enter-active {
    @media screen and (min-width: 561px) {
        transition: all 0.2s ease;
        width: 420px;
    }
}

.slide-fade-leave-active {
    @media screen and (min-width: 561px) {
        transition: all 0.2s ease-out;
        width: 420px;
    }
}

.slide-fade-enter,
.slide-fade-leave-to {
    opacity: 0;
    @media screen and (min-width: 561px) {
        width: 0;
        transform: translateX(5px);
    }
}
</style>
<style lang="scss" scoped>
.ReaderSidebarPlugin {
    z-index: $z2;
    height: calc(100vh - 1rem);
    background-color: var(--theme-background-color);
    color: var(--theme-text-color);
    opacity: 1;

    &.uiHidden {
        border: none !important;
        opacity: 0;
        overflow: hidden;
        transform: translateX(100%);
    }

    @media screen and (max-width: 560px) {
        position: static;
    }
}

.ReaderSidebarPluginContent {
    min-width: 420px;
    max-width: 420px;
    height: calc(100vh - 1rem);
    background-color: var(--theme-background-color);
    opacity: 0;
    transform: translateX(100%);

    @media screen and (max-width: 560px) {
        max-width: 100%;
    }

    @media screen and (max-width: 560px) {
        transform: translateX(0%);
        position: absolute;
        top: 0;
        left: 0;
        max-width: 100%;
        min-width: 100%;
        width: 100%;
        z-index: $z6;
    }

    h3 {
        margin: 0;
        color: var(--theme-title-color);
    }

    &.uiOpen {
        opacity: 1;
        transform: translateX(0%);
        animation: openWithOpacity 200ms;
    }

    .actionHeader {
        border-bottom: 1px solid var(--theme-border-color);
        @media screen and (max-width: 560px) {
            box-shadow: 0 3px 6px rgba(#000, 0.16);
            padding: 0.8rem 0;

            > div {
                order: 2;
            }
        }
    }

    .sidebar-grid {
        display: grid;
        grid-template-rows: auto 1fr;
        height: 100%;
        width: 100%;
        max-height: calc(100% - 3.5rem + 5px);
        position: absolute;
        bottom: 0;
        @media screen and (max-width: 560px) {
            max-height: calc(100% - 3.5rem + 5px);
        }
    }

    .debugMode {
        background-color: #f9f9f9;
        border-top: solid 5px;
        border-image: repeating-linear-gradient(-75deg, yellow, yellow 10px, black 10px, black 20px) 20;

        .debugger-controls {
            display: flex;
            gap: 10px;
            margin: 10px 0;
            padding: 10px;
        }

        input {
            padding: 10px;
            border: 1px solid #dddddd;
            border-radius: 5px;
            font-size: 14px;
            flex: 1;

            &:focus-visible {
                outline: none;
                box-shadow: 0 0 0 2px #3b82f6;
            }
        }

        button {
            background-color: #007bff;
            color: #ffffff;
            border: none;
            padding: 10px 15px;
            font-size: 14px;
            cursor: pointer;
            border-radius: 5px;
            transition: background-color 0.3s;

            &:hover {
                background-color: #0056b3;
            }
        }

        .reload-button {
            background-color: #28a745;

            &:hover {
                background-color: #218838;
            }
        }
    }

    iframe {
        border: none;
    }
}

@keyframes openWithOpacity {
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
}
</style>
